/**
 * DTEConfigTab — Configuración de Tipos de Documento Electrónico por tenant.
 * Panel izquierdo: lista de 12 tipos DTE con toggle on/off.
 * Panel derecho: campos configurables agrupados por zona para el tipo seleccionado.
 */
import { useState } from 'react'
import { FileText, ChevronRight, Check, Loader2, Info } from 'lucide-react'
import {
  useDTETypes,
  useDTEFieldSchema,
  useUpdateDTEType,
} from '@/modules/config/hooks/useConfig'
import type { DTETypeConfigItem, DTEFieldDef } from '@/modules/config/hooks/useConfig'

const OBLIGATION_LABELS: Record<number, { label: string; color: string }> = {
  1: { label: 'Obligatorio', color: 'bg-danger-surface text-danger-content border-danger-border' },
  2: { label: 'Condicional', color: 'bg-warning-surface text-warning-content border-warning-border' },
  3: { label: 'Opcional', color: 'bg-success-surface text-success-content border-success-border' },
}

const ZONE_LABELS: Record<string, string> = {
  'encabezado.id_doc': 'Identificación del Documento',
  'encabezado.emisor': 'Emisor',
  'encabezado': 'Encabezado',
  'encabezado.receptor': 'Receptor',
  'encabezado.transporte': 'Transporte',
  'encabezado.aduana': 'Aduana / Exportación',
  'encabezado.totales': 'Totales e Impuestos',
  'detalle': 'Detalle (Líneas)',
  'sub_tot_info': 'Subtotales Informativos',
  'dsc_rcg_global': 'Descuentos / Recargos Globales',
  'referencia': 'Referencias',
  'comisiones': 'Comisiones',
  'otra_moneda': 'Otra Moneda',
}

export function DTEConfigTab() {
  const { data: dteTypes, isLoading } = useDTETypes()
  const [selectedCode, setSelectedCode] = useState<number | null>(null)
  const updateMutation = useUpdateDTEType()

  const selectedType = dteTypes?.find(t => t.sii_doc_type === selectedCode)

  const handleToggleActive = (item: DTETypeConfigItem) => {
    updateMutation.mutate({
      sii_code: item.sii_doc_type,
      is_active: !item.is_active,
    })
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="animate-spin text-primary" size={24} />
      </div>
    )
  }

  return (
    <div data-tour="dte-types-page" className="flex flex-col lg:flex-row gap-6">
      {/* Left Panel: Document Types */}
      <div data-tour="dte-types-list" className="lg:w-80 flex-shrink-0">
        <div className="bg-glass backdrop-blur-glass border border-divider rounded-card overflow-hidden shadow-card">
          <div className="px-4 py-3 bg-surface-muted border-b border-divider">
            <h3 className="text-xs font-semibold text-content-muted uppercase tracking-wider">
              Tipos de Documento
            </h3>
            <p className="text-[10px] text-content-subtle mt-0.5">
              Activa los DTEs que emite tu empresa
            </p>
          </div>
          <div className="divide-y divide-divider max-h-[600px] overflow-y-auto">
            {(dteTypes || []).map(item => (
              <div
                key={item.sii_doc_type}
                className={`flex items-center gap-3 px-4 py-2.5 cursor-pointer transition-colors ${
                  selectedCode === item.sii_doc_type
                    ? 'bg-primary-50 border-l-2 border-l-primary'
                    : 'hover:bg-soft border-l-2 border-l-transparent'
                }`}
                onClick={() => setSelectedCode(item.sii_doc_type)}
              >
                {/* Toggle */}
                <button
                  onClick={(e) => { e.stopPropagation(); handleToggleActive(item) }}
                  className={`relative w-8 h-[18px] rounded-full flex-shrink-0 transition-colors ${
                    item.is_active ? 'bg-primary' : 'bg-content-subtle'
                  }`}
                  title={item.is_active ? 'Desactivar' : 'Activar'}
                >
                  <div className={`absolute top-0.5 w-3.5 h-3.5 bg-surface rounded-full shadow transition-transform ${
                    item.is_active ? 'translate-x-[17px]' : 'translate-x-0.5'
                  }`} />
                </button>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span className={`text-xs font-medium ${item.is_active ? 'text-content' : 'text-content-subtle'}`}>
                      {item.display_name}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-[10px] text-content-subtle font-mono">
                      SII {item.sii_doc_type}
                    </span>
                    <span className="text-[10px] text-content-subtle">
                      {item.enabled_field_count}/{item.configurable_field_count} campos
                    </span>
                  </div>
                </div>

                <ChevronRight size={14} className={`flex-shrink-0 ${
                  selectedCode === item.sii_doc_type ? 'text-primary' : 'text-content-subtle'
                }`} />
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right Panel: Field Configuration */}
      <div data-tour="dte-types-fields" className="flex-1 min-w-0">
        {selectedCode && selectedType ? (
          <FieldConfigPanel
            siiCode={selectedCode}
            config={selectedType}
          />
        ) : (
          <div className="bg-glass backdrop-blur-glass border border-divider rounded-card p-8 text-center shadow-card">
            <FileText className="mx-auto text-content-subtle mb-3" size={40} />
            <p className="text-sm text-content-muted">
              Selecciona un tipo de documento para configurar sus campos
            </p>
          </div>
        )}
      </div>
    </div>
  )
}


function FieldConfigPanel({ siiCode, config }: { siiCode: number; config: DTETypeConfigItem }) {
  const { data: schema, isLoading } = useDTEFieldSchema(siiCode)
  const updateMutation = useUpdateDTEType()
  const [expandedZones, setExpandedZones] = useState<Record<string, boolean>>({})

  const enabledSet = new Set(config.enabled_fields)

  const toggleZone = (zone: string) => {
    setExpandedZones(prev => ({ ...prev, [zone]: !prev[zone] }))
  }

  const toggleField = (fieldKey: string, field: DTEFieldDef) => {
    const newEnabled = new Set(enabledSet)
    if (newEnabled.has(fieldKey)) {
      newEnabled.delete(fieldKey)
    } else {
      newEnabled.add(fieldKey)
      // Auto-enable dependencies
      for (const dep of field.depends_on) {
        newEnabled.add(dep)
      }
    }
    updateMutation.mutate({
      sii_code: siiCode,
      enabled_fields: Array.from(newEnabled),
    })
  }

  if (isLoading || !schema) {
    return (
      <div className="bg-glass backdrop-blur-glass border border-divider rounded-card p-8 flex items-center justify-center shadow-card">
        <Loader2 className="animate-spin text-primary" size={24} />
      </div>
    )
  }

  const zones = Object.entries(schema.fields_by_zone)

  return (
    <div className="bg-glass backdrop-blur-glass border border-divider rounded-card overflow-hidden shadow-card">
      <div className="px-4 py-3 bg-surface-muted border-b border-divider">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-semibold text-content">
              {config.display_name}
            </h3>
            <p className="text-xs text-content-subtle mt-0.5">
              SII tipo {siiCode} &middot; Código interno: {config.internal_code}
            </p>
          </div>
          <div className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${
            config.is_active ? 'bg-success-surface text-success-content' : 'bg-surface-muted text-content-muted'
          }`}>
            {config.is_active ? 'Activo' : 'Inactivo'}
          </div>
        </div>
      </div>

      <div className="divide-y divide-divider max-h-[550px] overflow-y-auto">
        {zones.map(([zone, fields]) => {
          const isExpanded = expandedZones[zone] ?? false
          const zoneLabel = ZONE_LABELS[zone] || zone
          const enabledCount = fields.filter(f => f.obligation === 1 || enabledSet.has(f.key)).length

          return (
            <div key={zone}>
              <button
                onClick={() => toggleZone(zone)}
                className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-soft transition-colors"
              >
                <div className="flex items-center gap-2">
                  <ChevronRight size={14} className={`text-content-subtle transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                  <span className="text-sm font-medium text-content">{zoneLabel}</span>
                  <span className="text-[11px] bg-surface-muted text-content-muted px-1.5 rounded-full">
                    {enabledCount}/{fields.length}
                  </span>
                </div>
              </button>

              {isExpanded && (
                <div className="px-4 pb-3 space-y-1">
                  {fields.map(field => (
                    <FieldRow
                      key={field.key}
                      field={field}
                      isEnabled={field.obligation === 1 || enabledSet.has(field.key)}
                      isMandatory={field.obligation === 1}
                      onToggle={() => toggleField(field.key, field)}
                      disabled={!config.is_active || updateMutation.isPending}
                    />
                  ))}
                </div>
              )}
            </div>
          )
        })}
      </div>

      {updateMutation.isPending && (
        <div className="px-4 py-2 bg-primary-50 border-t border-primary-100 flex items-center gap-2">
          <Loader2 className="animate-spin text-primary" size={12} />
          <span className="text-[10px] text-primary">Guardando...</span>
        </div>
      )}
    </div>
  )
}


function FieldRow({ field, isEnabled, isMandatory, onToggle, disabled }: {
  field: DTEFieldDef
  isEnabled: boolean
  isMandatory: boolean
  onToggle: () => void
  disabled: boolean
}) {
  const obligation = OBLIGATION_LABELS[field.obligation]

  return (
    <div className={`flex items-start gap-3 py-1.5 pl-6 rounded transition-colors ${
      isEnabled ? '' : 'opacity-60'
    }`}>
      {/* Checkbox */}
      <button
        onClick={onToggle}
        disabled={disabled || isMandatory}
        className={`mt-0.5 w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 transition-colors ${
          isMandatory
            ? 'bg-surface-muted border-divider-strong cursor-not-allowed'
            : isEnabled
              ? 'bg-primary border-primary text-on-primary cursor-pointer'
              : 'border-divider-strong hover:border-primary-200 cursor-pointer'
        }`}
      >
        {(isEnabled || isMandatory) && <Check size={10} />}
      </button>

      {/* Label */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="text-sm text-content">{field.label}</span>
          <span className="text-[10px] font-mono text-content-subtle">{field.xml_tag}</span>
          {obligation && (
            <span className={`text-[10px] px-1.5 py-px rounded border ${obligation.color}`}>
              {obligation.label}
            </span>
          )}
          {field.is_zone_toggle && (
            <span className="text-[10px] px-1.5 py-px rounded bg-purple-50 dark:bg-purple-900/30 text-purple-600 dark:text-purple-300 border border-purple-200 dark:border-purple-800">
              Zona
            </span>
          )}
        </div>
        {field.description && (
          <p className="text-xs text-content-subtle mt-0.5 flex items-start gap-1">
            <Info size={11} className="mt-0.5 flex-shrink-0" />
            {field.description}
          </p>
        )}
      </div>
    </div>
  )
}
